import { useEffect } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../../App";
import {
  LayoutDashboard,
  Users as UsersIcon,
  CreditCard,
  FileText,
  ShieldAlert,
  Activity,
  ScrollText,
  LogOut,
} from "lucide-react";

/**
 * Admin shell. Gates access on:
 *   - auth not loading
 *   - user object is fully populated (strict string check on email)
 *   - user.is_admin === true (set by backend /api/auth/me when email is in ADMIN_EMAILS)
 *
 * Non-admin → redirect to "/" (home). No "undefined" flashes: we render a skeleton
 * until `user` is provably admin or provably not.
 */

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, exact: true, enabled: true },
  { to: "/admin/users", label: "Users", icon: UsersIcon, enabled: true },
  { to: "/admin/payments", label: "Payments", icon: CreditCard, enabled: true },
  { to: "/admin/content", label: "Content", icon: FileText, enabled: true },
  { to: "/admin/failed-signups", label: "Failed Signups", icon: ShieldAlert, enabled: true },
  { to: "/admin/system-health", label: "System Health", icon: Activity, enabled: false },
  { to: "/admin/audit-log", label: "Audit Log", icon: ScrollText, enabled: true },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Strict readiness: matches Dashboard.js lesson — truthy-only lets half-hydrated
  // cached users through and flashes "undefined".
  const isUserReady = !!user && typeof user.email === "string" && user.email.length > 0;
  const isAdmin = isUserReady && user.is_admin === true;

  useEffect(() => {
    if (!loading && isUserReady && !isAdmin) {
      navigate("/", { replace: true });
    }
    if (!loading && !isUserReady) {
      navigate("/login", { replace: true });
    }
  }, [loading, isUserReady, isAdmin, navigate]);

  if (loading || !isUserReady || !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="admin-layout-loading">
        <div className="h-12 w-12 rounded-full border-4 border-violet-300 border-t-violet-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="hidden md:flex md:w-64 flex-col bg-slate-900 text-slate-100">
        <div className="h-16 flex items-center px-6 border-b border-slate-800">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-pink-500" />
            <span className="font-bold text-sm">ColorWonder Admin</span>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active = item.exact
              ? location.pathname === item.to
              : location.pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition ${
                  active
                    ? "bg-violet-600 text-white"
                    : "text-slate-300 hover:bg-slate-800"
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{item.label}</span>
                {!item.enabled && (
                  <span className="ml-auto text-[10px] uppercase tracking-wide text-slate-500">soon</span>
                )}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="text-xs text-slate-400 px-3 pb-2 truncate">{user.email}</div>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-800"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Mobile nav */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-slate-900 text-slate-100 border-b border-slate-800">
        <div className="flex items-center gap-2 overflow-x-auto px-3 h-14">
          <span className="font-bold text-xs mr-2 whitespace-nowrap">CW Admin</span>
          {NAV.map((item) => {
            const active = item.exact
              ? location.pathname === item.to
              : location.pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`whitespace-nowrap px-3 py-1 rounded-full text-xs ${
                  active ? "bg-violet-600 text-white" : "text-slate-300"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </div>
      </div>

      <main className="flex-1 min-w-0 md:ml-0 pt-14 md:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
